import React, { useState, useEffect } from 'react';
import { X, Send, User, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import type { CreateMessageForm } from '../../types/messaging';
import type { User as AppUser } from '../../types';

interface MessageComposerProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (messageData: CreateMessageForm) => Promise<void>;
  replyTo?: {
    messageId: string;
    subject: string;
    recipientId: string;
    recipientName: string;
  };
  isLoading?: boolean;
}

export const MessageComposer: React.FC<MessageComposerProps> = ({
  isOpen,
  onClose,
  onSend,
  replyTo,
  isLoading = false
}) => {
  const { user } = useAuthStore();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<CreateMessageForm>({
    recipient_id: '',
    subject: '',
    content: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      loadUsers();
      
      // Pre-fill for reply
      if (replyTo) {
        setFormData({
          recipient_id: replyTo.recipientId,
          subject: replyTo.subject.startsWith('Re: ') ? replyTo.subject : `Re: ${replyTo.subject}`,
          content: '',
          parent_message_id: replyTo.messageId
        });
      } else {
        setFormData({
          recipient_id: '',
          subject: '',
          content: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, replyTo]);

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .not('profile_type', 'is', null)
        .neq('id', user?.id)
        .order('full_name');

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      // Mock data for development
      setUsers([
        {
          id: 'coord-1',
          full_name: 'Sophie Martin',
          email: 'sophie.martin@cress-guadeloupe.org',
          profile_type: 'ACCOMPAGNATEUR',
          organization: 'CRESS Guadeloupe',
          phone: null,
          address: null,
          city: null,
          postal_code: null,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        },
        {
          id: 'coord-2',
          full_name: 'Jean-Claude Martin',
          email: 'coordination@cress-guadeloupe.org',
          profile_type: 'COORDINATEUR',
          organization: 'CRESS Guadeloupe',
          phone: null,
          address: null,
          city: null,
          postal_code: null,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        }
      ]);
    }
  };

  const filteredUsers = users.filter(u => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      u.full_name?.toLowerCase().includes(searchLower) ||
      u.email?.toLowerCase().includes(searchLower) ||
      u.organization?.toLowerCase().includes(searchLower)
    );
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.recipient_id) {
      newErrors.recipient_id = 'Veuillez sélectionner un destinataire';
    }
    if (!formData.subject.trim()) {
      newErrors.subject = 'Le sujet est requis';
    }
    if (!formData.content.trim()) {
      newErrors.content = 'Le message ne peut pas être vide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      await onSend(formData);
      onClose();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleInputChange = (field: keyof CreateMessageForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getProfileTypeColor = (type: string) => {
    const colors = {
      PORTEUR: 'bg-green-100 text-green-800',
      ACCOMPAGNATEUR: 'bg-blue-100 text-blue-800',
      COORDINATEUR: 'bg-purple-100 text-purple-800',
      FINANCEUR: 'bg-orange-100 text-orange-800',
      ADMIN: 'bg-red-100 text-red-800'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getProfileTypeLabel = (type: string) => {
    const labels = {
      PORTEUR: 'Porteur',
      ACCOMPAGNATEUR: 'Accompagnateur',
      COORDINATEUR: 'Coordinateur',
      FINANCEUR: 'Financeur',
      ADMIN: 'Admin'
    };
    return labels[type as keyof typeof labels] || type;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {replyTo ? 'Répondre au message' : 'Nouveau message'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Recipient */}
          {!replyTo && (
            <div>
              <label htmlFor="recipient" className="block text-sm font-medium text-gray-700 mb-2">
                Destinataire *
              </label>
              
              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Rechercher un utilisateur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Users List */}
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg">
                {filteredUsers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <User className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>Aucun utilisateur trouvé</p>
                  </div>
                ) : (
                  filteredUsers.map((u) => (
                    <label
                      key={u.id}
                      className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <input
                        type="radio"
                        name="recipient"
                        value={u.id}
                        checked={formData.recipient_id === u.id}
                        onChange={(e) => handleInputChange('recipient_id', e.target.value)}
                        className="mr-3"
                      />
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {u.full_name?.charAt(0) || u.email?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <p className="font-medium text-gray-900">
                              {u.full_name || u.email}
                            </p>
                            {u.profile_type && (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getProfileTypeColor(u.profile_type)}`}>
                                {getProfileTypeLabel(u.profile_type)}
                              </span>
                            )}
                          </div>
                          {u.organization && (
                            <p className="text-sm text-gray-600">{u.organization}</p>
                          )}
                        </div>
                      </div>
                    </label>
                  ))
                )}
              </div>
              {errors.recipient_id && (
                <p className="mt-1 text-sm text-red-600">{errors.recipient_id}</p>
              )}
            </div>
          )}

          {/* Reply Info */}
          {replyTo && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>En réponse à :</strong> {replyTo.recipientName}
              </p>
              <p className="text-xs text-blue-600 mt-1">"{replyTo.subject}"</p>
            </div>
          )}

          {/* Subject */}
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
              Sujet *
            </label>
            <input
              id="subject"
              type="text"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.subject ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Objet de votre message"
            />
            {errors.subject && (
              <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
            )}
          </div>

          {/* Content */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
              Message *
            </label>
            <textarea
              id="content"
              value={formData.content}
              onChange={(e) => handleInputChange('content', e.target.value)}
              rows={8}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.content ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Rédigez votre message..."
            />
            {errors.content && (
              <p className="mt-1 text-sm text-red-600">{errors.content}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {formData.content.length} caractères
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Envoi...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Envoyer</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};